import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { Plus, AirplaneTilt, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABELS = {
    vacaciones: "Vacaciones",
    ausencia: "Ausencia personal",
    baja: "Baja médica",
    formacion: "Formación",
    personal: "Personal",
};

const STATUS_STYLES = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
};

const STATUS_LABEL = {
    pending: "Pendiente",
    approved: "Aprobada",
    rejected: "Rechazada",
};

export default function Absences() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        type: "vacaciones",
        start_date: today,
        end_date: today,
        hours_per_day: 8,
        reason: "",
    });

    const load = async () => {
        const res = await http.get("/absences");
        setItems(res.data);
    };
    useEffect(() => {
        load();
    }, []);

    const accessors = useMemo(
        () => ({
            type: (a) => TYPE_LABELS[a.type] || a.type,
            dates: (a) => a.start_date || "",
            days: (a) => Number(a.days || 0),
            reason: (a) => (a.reason || "").toLowerCase(),
            status: (a) => STATUS_LABEL[a.status] || a.status,
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        items,
        "absences-list",
        accessors,
        { initialKey: "dates", initialDir: "desc" },
    );

    const submit = async (e) => {
        e.preventDefault();
        try {
            await http.post("/absences", form);
            toast.success("Solicitud enviada");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al enviar");
        }
    };

    const remove = async (a) => {
        const isOwnerPending = a.user_id === user?.user_id && a.status === "pending";
        const canDelete = isAdmin || isOwnerPending;
        if (!canDelete) return;
        const msg = isAdmin
            ? `¿Eliminar definitivamente la solicitud de ${a.user_name || "este usuario"} (${a.start_date} → ${a.end_date})?`
            : "¿Cancelar tu solicitud pendiente?";
        if (!window.confirm(msg)) return;
        try {
            await http.delete(`/absences/${a.request_id}`);
            toast.success(isAdmin ? "Solicitud eliminada" : "Solicitud cancelada");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    return (
        <div data-testid="page-absences">
            <PageHeader
                eyebrow="Vacaciones y ausencias"
                title="Mis solicitudes"
                description="Solicita vacaciones, ausencias o bajas. Un administrador las revisará."
                actions={
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                data-testid="absence-new-btn"
                            >
                                <Plus size={14} className="mr-2" /> Nueva solicitud
                            </Button>
                        </SheetTrigger>
                        <SheetContent className="rounded-none sm:max-w-md">
                            <SheetHeader>
                                <SheetTitle className="font-display">
                                    Nueva solicitud
                                </SheetTitle>
                                <SheetDescription className="text-xs text-slate-500">
                                    Indica fechas y motivo. Un administrador revisará tu solicitud.
                                </SheetDescription>
                            </SheetHeader>
                            <form
                                onSubmit={submit}
                                className="mt-6 space-y-4"
                                data-testid="absence-form"
                            >
                                <div>
                                    <Label className="text-xs">Tipo</Label>
                                    <Select
                                        value={form.type}
                                        onValueChange={(v) =>
                                            setForm({ ...form, type: v })
                                        }
                                    >
                                        <SelectTrigger
                                            className="mt-1 rounded-sm border-slate-300"
                                            data-testid="absence-type"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(TYPE_LABELS)
                                                .filter(([k]) => k === "vacaciones")
                                                .map(([k, v]) => (
                                                    <SelectItem key={k} value={k}>
                                                        {v}
                                                    </SelectItem>
                                                ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Inicio</Label>
                                        <Input
                                            type="date"
                                            value={form.start_date}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    start_date: e.target.value,
                                                })
                                            }
                                            className="mt-1 rounded-sm border-slate-300 font-mono"
                                            data-testid="absence-start"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Fin</Label>
                                        <Input
                                            type="date"
                                            value={form.end_date}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    end_date: e.target.value,
                                                })
                                            }
                                            className="mt-1 rounded-sm border-slate-300 font-mono"
                                            data-testid="absence-end"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs">Horas / día</Label>
                                    <Input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="24"
                                        value={form.hours_per_day}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                hours_per_day: Number(e.target.value),
                                            })
                                        }
                                        className="mt-1 rounded-sm border-slate-300 font-mono"
                                        data-testid="absence-hours"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Motivo (opcional)</Label>
                                    <Textarea
                                        rows={3}
                                        value={form.reason}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                reason: e.target.value,
                                            })
                                        }
                                        className="mt-1 rounded-sm border-slate-300"
                                        data-testid="absence-reason"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                    data-testid="absence-submit"
                                >
                                    Enviar solicitud
                                </Button>
                            </form>
                        </SheetContent>
                    </Sheet>
                }
            />
            <div className="p-6 lg:p-10">
                <div className="border border-slate-200 bg-white">
                    <table className="w-full text-sm" data-testid="absences-list">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("type", "py-3 px-4 text-left")}>
                                    Tipo
                                    <SortIndicator active={sortKey === "type"} dir={sortDir} />
                                </th>
                                <th {...headerProps("dates", "py-3 px-4 text-left")}>
                                    Fechas
                                    <SortIndicator active={sortKey === "dates"} dir={sortDir} />
                                </th>
                                <th {...headerProps("days", "py-3 px-4 text-right")}>
                                    Días
                                    <SortIndicator active={sortKey === "days"} dir={sortDir} />
                                </th>
                                <th {...headerProps("reason", "py-3 px-4 text-left")}>
                                    Motivo
                                    <SortIndicator active={sortKey === "reason"} dir={sortDir} />
                                </th>
                                <th {...headerProps("status", "py-3 px-4 text-left")}>
                                    Estado
                                    <SortIndicator active={sortKey === "status"} dir={sortDir} />
                                </th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="text-center py-16 text-slate-500"
                                    >
                                        <AirplaneTilt
                                            size={28}
                                            className="mx-auto text-slate-300 mb-3"
                                        />
                                        Aún no has solicitado nada.
                                    </td>
                                </tr>
                            )}
                            {sorted.map((a) => {
                                const isOwnerPending =
                                    a.user_id === user?.user_id && a.status === "pending";
                                const canDelete = isAdmin || isOwnerPending;
                                return (
                                    <tr
                                        key={a.request_id}
                                        className="border-b border-slate-100 hover:bg-slate-50"
                                        data-testid={`absence-row-${a.request_id}`}
                                    >
                                        <td className="py-3 px-4 text-slate-800 font-medium">
                                            {TYPE_LABELS[a.type] || a.type}
                                        </td>
                                        <td className="py-3 px-4 font-mono text-slate-700">
                                            {a.start_date} → {a.end_date}
                                        </td>
                                        <td className="py-3 px-4 text-right font-mono tabular-nums text-slate-900 font-semibold">
                                            {a.days}
                                        </td>
                                        <td className="py-3 px-4 text-slate-600 max-w-xs truncate">
                                            {a.reason || "—"}
                                        </td>
                                        <td className="py-3 px-4">
                                            <span
                                                className={`px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[a.status]}`}
                                            >
                                                {STATUS_LABEL[a.status]}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            {canDelete && (
                                                <button
                                                    onClick={() => remove(a)}
                                                    title={
                                                        isAdmin
                                                            ? "Eliminar solicitud"
                                                            : "Cancelar solicitud pendiente"
                                                    }
                                                    className="text-slate-400 hover:text-red-600"
                                                    data-testid={`absence-delete-${a.request_id}`}
                                                >
                                                    <Trash size={16} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
